"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="flex items-center">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-default uppercase text-xs tracking-[0.18em] px-4 py-2 hover:text-primary transition-colors duration-200">
                {item.label}
              </Link>
            </NavigationMenuLink>
            {i < menuItems.length - 1 && <span aria-hidden="true" className="inline-block w-1 h-1 bg-primary" />}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-6 w-32 bg-muted animate-pulse" />
        <div className="h-6 w-32 bg-muted animate-pulse" />
        <div className="h-6 w-32 bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col px-6 py-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="text-foreground font-default uppercase text-sm tracking-[0.18em] py-4 border-b border-border hover:text-primary transition-colors duration-200">
          <span className="text-primary mr-3 font-default text-xs">
            § {String(i + 1).padStart(2, "0")}
          </span>
          {item.label}
        </Link>)}
    </nav>;
}
export default function CommandCenterHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1282" id="command-center-header" className="sticky top-0 z-50 w-full transition-all duration-300 bg-background border-b border-border !h-[150px]">
      <div aria-hidden="true" className="absolute top-0 right-6 hidden md:flex items-center gap-1 pointer-events-none" style={{
      transform: "rotate(-6deg)",
      transformOrigin: "right top"
    }}>
        <span className="block w-16 h-px bg-primary" />
        <span className="block w-1.5 h-1.5 bg-primary" />
      </div>

      <div className="mx-auto max-w-[1440px] px-6 grid grid-cols-12 items-center !h-[128px] md:px-12">
        <div className="col-span-6 md:col-span-3 flex items-center">
          <Link to="/" className="flex items-center" aria-label="AI Guyz">
            <WvcLogo className="text-white !w-[300px] !h-[150px] [&_svg]:text-white [&_svg]:!w-[300px] [&_svg]:!h-[150px]" fill="white" />
          </Link>
        </div>

        <div className="hidden md:flex col-span-6 items-center justify-center">
          <MenuProvider menu_id="44">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-3">
          <Link to="#" className="hidden sm:inline-flex">
            <Button variant="default" className="bg-primary text-primary-foreground font-default uppercase tracking-[0.04em] px-6 md:px-9 py-5 h-auto rounded-lg border border-primary hover:bg-accent hover:text-accent-foreground transition-all duration-200">
              Get Started
            </Button>
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="md:hidden text-foreground hover:bg-transparent hover:text-primary rounded-lg">
                <Menu className="size-6" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-l border-border w-[85%] sm:max-w-sm p-0">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                <SheetTitle className="font-default uppercase text-xs tracking-[0.18em] text-muted-foreground text-left">
                  § Menu / AI Guyz
                </SheetTitle>
              </SheetHeader>

              <MenuProvider menu_id="44">
                <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
              </MenuProvider>

              <div className="px-6 pt-4 pb-8 mt-auto">
                <Link to="#" onClick={() => setMobileOpen(false)} className="block">
                  <Button variant="default" className="w-full bg-primary text-primary-foreground font-default uppercase tracking-[0.04em] py-5 h-auto rounded-lg border border-primary hover:bg-accent hover:text-accent-foreground transition-colors duration-200">
                    Get Started
                  </Button>
                </Link>

                <div className="mt-6 flex items-center gap-2 font-default uppercase text-[0.65rem] tracking-[0.14em] text-muted-foreground">
                  <span className="inline-block w-1 h-1 bg-primary" />
                  <span>Vacaville, CA</span>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}